import { ChangeEvent, useCallback } from "react";
import clsx from "clsx";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { LayerTypeControlPageProps } from "../../types";
import { useIsochroneLayerTypeContext } from "./IsochroneContext";
import { RoutingTime, RoutingType } from "./types";
import { useControlTracking } from "../../ControlTrackingContext";

export function IsochroneControlPage({ disabled }: LayerTypeControlPageProps) {
  const { t } = useTranslation();
  const { trackControlChange } = useControlTracking();

  const { routingType, setRoutingType, routingTime, setRoutingTime, fetching } = useIsochroneLayerTypeContext();

  const setRoutingTypeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      trackControlChange({ filterName: "routingType", filterState: e.currentTarget.value });
      setRoutingType(e.currentTarget.value as RoutingType);
    },
    [setRoutingType, trackControlChange]
  );

  const setRoutingTimeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      trackControlChange({ filterName: "routingTime", filterState: e.currentTarget.value });
      setRoutingTime(e.currentTarget.value as RoutingTime);
    },
    [setRoutingTime, trackControlChange]
  );

  return (
    <article
      className={clsx("p-2 rounded-sm bg-white flex flex-col gap-y-1", disabled && "pointer-events-none grayscale")}
    >
      <section className={clsx("flex flex-col gap-y-1", disabled && "opacity-50")}>
        <h1 className="text-sm text-text-base-primary">{t("distance_and_travel.control_page.routing_type.header")}</h1>
        <div className="flex gap-x-1">
          {Object.values(RoutingType).map((type) => (
            <div key={type} className="flex items-center pl-2 gap-x-1">
              <input
                type="radio"
                disabled={disabled || fetching}
                id={type}
                name={type}
                value={type}
                checked={type === routingType}
                onChange={setRoutingTypeHandler}
              />
              <label htmlFor={type} className="text-sm text-text-base-secondary">
                {t(`distance_and_travel.control_page.routing_type.label.${type}`)}
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className={clsx("flex flex-col gap-y-1", disabled && "opacity-50")}>
        <h1 className="text-sm text-text-base-primary">{t("distance_and_travel.control_page.routing_time.header")}</h1>
        <div className="flex gap-x-1">
          {Object.values(RoutingTime).map((time) => (
            <div key={time} className="flex items-center pl-2 capitalize gap-x-1">
              <input
                type="radio"
                disabled={disabled || fetching}
                id={time}
                name={time}
                value={time}
                checked={time === routingTime}
                onChange={setRoutingTimeHandler}
              />
              <label htmlFor={time} className="text-sm text-text-base-secondary">
                {t(`distance_and_travel.control_page.routing_time.label.${time}`)}
              </label>
            </div>
          ))}
        </div>
      </section>
      {fetching && <div className="absolute inset-0 cursor-wait" />}
    </article>
  );
}
